import { Box, Divider, Typography } from '@mui/material';
import { Component, ErrorInfo, ReactNode, useState } from 'react';
import { Field } from 'src/API';
import { FIELD_TYPES, FieldInput } from 'src/api/process-module';
import logger from 'src/log/log';
import { useSelector } from 'src/store/index';
import { forceString } from 'src/utils/forceString';
import { BaseFieldWrapper } from './preview/BaseFieldWrapper';
import { CheckboxListField } from './preview/CheckboxListField';
import { CommentField } from './preview/CommentField';
import { DropdownField } from './preview/DropdownField';
import DynamicTable from './preview/DynamicTable';
import { getConfigForType } from './preview/getConfigForType';
import { Image } from './preview/Image';
import { LinkField } from './preview/LinkField';
import { MarkdownField } from './preview/MarkdownField';
import { ProcessModuleInfiniteCanvasPreview } from './preview/ProcessModuleInfiniteCanvasPreview';
import { Video } from './preview/video';

interface FormFieldPreviewProps {
  field: FieldInput & Partial<Field>;
  value?: any;
  onChange?: (value: any) => void;
  disabled?: boolean;
}

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

class FormFieldPreviewErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logger.error('FormFieldPreview Error:', { error, errorInfo });
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box sx={{ p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
          <Typography color="error" variant="body2">
            Something went wrong rendering this field. Please try again or contact support if the issue persists.
          </Typography>
        </Box>
      );
    }

    return this.props.children;
  }
}

const getDefaultPlaceholder = (field: FieldInput) => {
  switch (field.type) {
    case FIELD_TYPES.DROPDOWN as 'DROPDOWN':
      return 'Select an option...';
    case FIELD_TYPES.CHECKBOX_LIST as 'CHECKBOX_LIST':
      return 'Select options...';
    case FIELD_TYPES.COMMENT as 'COMMENT':
      return 'Enter your comment...';
    case FIELD_TYPES.LINK as 'LINK':
      return 'Enter URL...';
    case FIELD_TYPES.HTML as 'HTML':
      return 'Enter HTML content...';
    case FIELD_TYPES.IMAGE as 'IMAGE':
      return 'Enter image URL...';
    case FIELD_TYPES.MARKDOWN as 'MARKDOWN':
      return 'Enter markdown content...';
    default:
      return `Enter ${field?.label?.toLowerCase()}...`;
  }
};

export const FormFieldPreview = ({
  field,
  value: externalValue,
  onChange: externalOnChange,
  disabled = false,
}: FormFieldPreviewProps) => {
  const [internalValue, setInternalValue] = useState<any>(null);
  const team = useSelector((state) => state.team.current);
  // Don't render if field is not visible
  if (field.properties?.visible === false) {
    return null;
  }

  const value = externalValue ?? internalValue;
  const onChange = externalOnChange ?? setInternalValue;
  const commonProps = {
    field: field as any,
    fieldProps: {
      ...field.properties,
      label: field.label,
      required: field.required,
      placeholder: field?.placeholder || getDefaultPlaceholder(field),
      fieldConfiguration: getConfigForType(field.type),
    },
    disabled,
    value,
    onChange,
  };

  const renderField = () => {
    switch (field.type) {
      case FIELD_TYPES.DROPDOWN as 'DROPDOWN':
        return (
          <DropdownField
            {...commonProps}
            fieldProps={{
              ...commonProps.fieldProps,
              type: FIELD_TYPES.DROPDOWN,
              options: field.dataSource ? JSON.parse(forceString(field.dataSource)) : [],
            }}
          />
        );

      case FIELD_TYPES.CHECKBOX_LIST as 'CHECKBOX_LIST':
        return (
          <CheckboxListField
            {...commonProps}
            value={value || {}}
            fieldProps={{
              ...commonProps.fieldProps,
              type: FIELD_TYPES.CHECKBOX_LIST,
              options: field.dataSource ? JSON.parse(forceString(field.dataSource)) : [],
            }}
          />
        );

      case FIELD_TYPES.COMMENT as 'COMMENT':
        return (
          <CommentField
            {...commonProps}
            fieldProps={{
              ...commonProps.fieldProps,
              type: FIELD_TYPES.COMMENT,
            }}
          />
        );

      case FIELD_TYPES.LINK as 'LINK':
        return (
          <LinkField
            {...commonProps}
            fieldProps={{
              ...commonProps.fieldProps,
              type: FIELD_TYPES.LINK,
            }}
            disabled={field.properties?.disabled}
            value={(field as any).properties?.dataSource || field.dataSource || ''}
          />
        );

      case FIELD_TYPES.HTML as 'HTML':
        return (
          <BaseFieldWrapper {...commonProps.fieldProps}>
            <div
              dangerouslySetInnerHTML={{
                __html: forceString((field as any).properties?.dataSource || field.dataSource) || '',
              }}
            />
          </BaseFieldWrapper>
        );

      case FIELD_TYPES.MARKDOWN as 'MARKDOWN':
        // Prioritize field.dataSource (current edits) over properties.dataSource (saved value)
        const markdownValue = (field.dataSource ?? (field as any).properties?.dataSource ?? '') as string;
        return (
          <MarkdownField field={field} fieldProps={commonProps.fieldProps} value={markdownValue} disabled={disabled} />
        );

      case FIELD_TYPES.IMAGE as 'IMAGE':
        const imageData = (() => {
          const source = (field as any).properties?.dataSource || field.dataSource;
          if (!source) return {};
          try {
            return JSON.parse(forceString(source));
          } catch {
            return {};
          }
        })();
        return (
          <BaseFieldWrapper {...commonProps.fieldProps}>
            <Image field={field} />
          </BaseFieldWrapper>
        );

      case FIELD_TYPES.VIDEO as 'VIDEO':
        return (
          <BaseFieldWrapper {...commonProps.fieldProps}>
            <Video field={field} />
          </BaseFieldWrapper>
        );

      case FIELD_TYPES.DYNAMIC_TABLE as 'DYNAMIC_TABLE':
        return (
          <BaseFieldWrapper {...commonProps.fieldProps}>
            <DynamicTable
              field={field as any}
              data={value || []}
              onChange={onChange}
              enableAddColumns
              enableAddRows
              enableEdit={!disabled}
            />
          </BaseFieldWrapper>
        );

      // case FIELD_TYPES.INFINITE_CANVAS as 'INFINITE_CANVAS':
      //   return (
      //     <ButtonLinkInfiniteCanvas
      //       teamId={team?.id || ''}
      //       formFieldConfig={field}
      //       onActionLink={(data) => onChange(data)}
      //       value={value}
      //       onActionUnlink={(data) => onChange(null)}
      //     />
      //   );

      case FIELD_TYPES.PROCESS_MODULE_INFINITE_CANVAS as 'PROCESS_MODULE_INFINITE_CANVAS':
        return (
          <ProcessModuleInfiniteCanvasPreview
            teamId={team?.id || ''}
            formFieldConfig={field}
            onActionLink={(data) => onChange(data)}
            value={value}
            onActionUnlink={(data) => onChange(null)}
          />
        );

      case FIELD_TYPES.DIVIDER as 'DIVIDER':
        return (
          <Box sx={{ width: '100%', my: 2 }}>
            <Divider />
          </Box>
        );

      default:
        return (
          <BaseFieldWrapper {...commonProps.fieldProps}>
            <Typography color="text.secondary">Preview not available for this field type: {field.type}</Typography>
          </BaseFieldWrapper>
        );
    }
  };

  return <FormFieldPreviewErrorBoundary>{renderField()}</FormFieldPreviewErrorBoundary>;
};
